/**
 * QuickViewModal — a Shadcn Dialog that surfaces enough product info to
 * make a "keep browsing vs. dive in" call without unmounting the browse
 * grid on /shop. Fires from the "Quick view" hover chip on a product
 * card. Full-detail link is still one click away.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X as XIcon } from "lucide-react";
import type { Product } from "@/lib/types";
import { productImage } from "@/lib/productImage";
import { useCurrency } from "@/contexts/CurrencyContext";
import { SibaScoreBadge } from "@/components/shared/SibaScoreBadge";
import { SmartImage } from "@/components/shared/SmartImage";
import { useSamplePool } from "@/lib/sampleImages";
import { VerifiedSellerBadge } from "@/components/shop/VerifiedSellerBadge";

interface Props {
  product: Product | null;
  open: boolean;
  onClose: () => void;
}

export const QuickViewModal: React.FC<Props> = ({ product, open, onClose }) => {
  const { formatPrice } = useCurrency();
  useSamplePool();
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the modal owns focus.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !product) return null;
  const p = product;
  const discount = p.compare_at_price && p.price && p.compare_at_price > p.price
    ? Math.round((1 - p.price / p.compare_at_price) * 100) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Quick view — ${p.title}`}
      data-testid="quick-view-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
    >
      <button
        type="button"
        aria-label="Close quick view"
        onClick={onClose}
        data-testid="quick-view-backdrop"
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl rounded-2xl">
        <button
          type="button"
          onClick={onClose}
          data-testid="quick-view-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-10 h-9 w-9 flex items-center justify-center rounded-full bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
        >
          <XIcon className="h-4 w-4" />
        </button>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
          <div className="md:col-span-3 relative bg-slate-50 dark:bg-slate-800 aspect-square md:aspect-auto md:min-h-[420px] overflow-hidden rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none">
            <SmartImage
              src={productImage(p as any, 900)}
              alt={p.title}
              wrapperClassName="absolute inset-0"
              className="w-full h-full object-cover"
              loading="eager"
              testId="quick-view-image"
            />
            {discount != null && (
              <span
                className="absolute top-3 left-3 bg-orange-500 text-white text-[11px] font-bold px-2.5 py-1 font-display shadow-sm"
                data-testid="quick-view-discount"
              >
                {discount}% OFF
              </span>
            )}
            {p.product_score != null && (
              <span
                className="absolute top-3 right-14 md:right-3"
                data-testid="quick-view-sibascore"
              >
                <SibaScoreBadge score={p.product_score} size="sm" />
              </span>
            )}
          </div>
          <div className="md:col-span-2 p-6 flex flex-col">
            <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-display font-semibold flex items-center gap-2">
              <span className="truncate">{p.brand || "Unbranded"}</span>
              {p.verified_brand && <VerifiedSellerBadge verified size="xs" showLabel={false} />}
            </div>
            <h2
              className="font-display font-semibold text-xl md:text-2xl text-slate-900 dark:text-white mt-2 leading-snug"
              data-testid="quick-view-title"
            >
              {p.title}
            </h2>
            {p.short_description && (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-4">
                {p.short_description}
              </p>
            )}
            <div className="mt-5 flex items-baseline gap-3 tabular-nums">
              <div
                className="font-display font-bold text-2xl md:text-3xl text-slate-900 dark:text-white"
                data-testid="quick-view-price"
              >
                {formatPrice(p.price || 0)}
              </div>
              {p.compare_at_price && p.compare_at_price > (p.price || 0) && (
                <div className="text-sm text-slate-400 dark:text-slate-500 line-through font-display">
                  {formatPrice(p.compare_at_price)}
                </div>
              )}
            </div>
            <div className="mt-auto pt-6 flex flex-col gap-2">
              <Link
                to={`/shop/p/${p.slug}`}
                onClick={onClose}
                data-testid="quick-view-view-details"
                className="w-full h-11 inline-flex items-center justify-center gap-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 font-semibold text-sm rounded-xl transition-colors"
              >
                View full details <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={onClose}
                data-testid="quick-view-keep-browsing"
                className="w-full h-11 inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-sm rounded-xl transition-colors"
              >
                Keep browsing
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
